import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import {
  useGetReportingApiEntitlementsQuery,
  useGrantReportingApiEntitlementMutation,
  useRevokeReportingApiEntitlementMutation,
  type ReportingApiEntitlementDataset,
} from 'src/pages/operations/endpoints/reporting-api-entitlements.endpoints';
import { SelectionCheckbox } from './selection-checkbox';
import { ManageClientApiKeysCard } from './manage-client-api-keys-card';

const DATASET_LABELS: Record<string, string> = {
  performance: 'Performance',
  geo: 'Geo',
  domain: 'Domain',
  flights: 'Flights',
  survey_definitions: 'Definitions',
  survey_results: 'Results',
  survey_demographics: 'Demographics',
  survey_verbatims: 'Verbatims',
  survey_trends: 'Trends',
  survey_export: 'Export',
  survey_authoring: 'Authoring',
};

function isSurveyDataset(dataset: string): boolean {
  return dataset.startsWith('survey_');
}

function formatDatasetLabel(dataset: string): string {
  if (DATASET_LABELS[dataset]) {
    return DATASET_LABELS[dataset];
  }
  if (isSurveyDataset(dataset)) {
    const suffix = dataset.slice('survey_'.length);
    return suffix.charAt(0).toUpperCase() + suffix.slice(1);
  }
  return dataset.charAt(0).toUpperCase() + dataset.slice(1);
}

function parentCheckboxState(
  grantable: ReportingApiEntitlementDataset[],
  entitled: ReportingApiEntitlementDataset[],
): 'checked' | 'unchecked' | 'indeterminate' {
  if (grantable.length === 0 || entitled.length === 0) {
    return 'unchecked';
  }
  if (entitled.length === grantable.length) {
    return 'checked';
  }
  return 'indeterminate';
}

type ExpandableSectionId = 'reporting-api' | 'surveys-api';

interface ManageClientApiAccessProps {
  disabled?: boolean;
}

/** Tree rows for Reporting API / Surveys API — embed inside the shared permissions border. */
export function ManageClientApiAccess({ disabled = false }: ManageClientApiAccessProps) {
  const { activeClient } = useActiveClient();
  const extClientId = activeClient?.ext_client_id;
  const [expandedIds, setExpandedIds] = useState<ExpandableSectionId[]>([]);
  const [pendingDatasets, setPendingDatasets] = useState<string[]>([]);

  const { data: entitlements, isLoading } = useGetReportingApiEntitlementsQuery(
    extClientId ?? '',
    { skip: !extClientId }
  );
  const [grantEntitlement] = useGrantReportingApiEntitlementMutation();
  const [revokeEntitlement] = useRevokeReportingApiEntitlementMutation();

  const datasets = entitlements?.datasets ?? [];
  const grantableDatasets = useMemo(
    () => datasets.filter((row) => row.grantable),
    [datasets]
  );
  const reportingDatasets = useMemo(
    () => grantableDatasets.filter((row) => !isSurveyDataset(row.dataset)),
    [grantableDatasets]
  );
  const surveyDatasets = useMemo(
    () => grantableDatasets.filter((row) => isSurveyDataset(row.dataset)),
    [grantableDatasets]
  );
  const entitledReportingDatasets = useMemo(
    () => reportingDatasets.filter((row) => row.entitled),
    [reportingDatasets]
  );
  const entitledSurveyDatasets = useMemo(
    () => surveyDatasets.filter((row) => row.entitled),
    [surveyDatasets]
  );

  const reportingParentState = parentCheckboxState(
    reportingDatasets,
    entitledReportingDatasets,
  );
  const surveysParentState = parentCheckboxState(
    surveyDatasets,
    entitledSurveyDatasets,
  );

  const toggleExpanded = (id: ExpandableSectionId) => {
    setExpandedIds((current) => (
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    ));
  };

  const withPending = async(dataset: string, action: () => Promise<unknown>) => {
    setPendingDatasets((current) => (
      current.includes(dataset) ? current : [...current, dataset]
    ));
    try {
      await action();
    } catch {
      // RTK global error handling surfaces API failures.
    } finally {
      setPendingDatasets((current) => current.filter((value) => value !== dataset));
    }
  };

  const handleDatasetToggle = async(dataset: string, entitled: boolean) => {
    if (disabled || !extClientId || pendingDatasets.includes(dataset)) {
      return;
    }
    const payload = { ext_client_id: extClientId, dataset };
    await withPending(dataset, async() => {
      if (entitled) {
        await revokeEntitlement(payload).unwrap();
      } else {
        await grantEntitlement(payload).unwrap();
      }
    });
  };

  const handleParentToggle = async(
    rows: ReportingApiEntitlementDataset[],
    parentState: 'checked' | 'unchecked' | 'indeterminate',
  ) => {
    if (disabled || !extClientId || rows.length === 0) {
      return;
    }

    const shouldGrantAll = parentState !== 'checked';
    const targets = shouldGrantAll
      ? rows.filter((row) => !row.entitled)
      : rows.filter((row) => row.entitled);

    await Promise.all(targets.map((row) => (
      withPending(row.dataset, async() => {
        const payload = { ext_client_id: extClientId, dataset: row.dataset };
        if (shouldGrantAll) {
          await grantEntitlement(payload).unwrap();
        } else {
          await revokeEntitlement(payload).unwrap();
        }
      })
    )));
  };

  if (!extClientId) {
    return null;
  }

  const reportingExpanded = expandedIds.includes('reporting-api');
  const surveysExpanded = expandedIds.includes('surveys-api');
  const parentBusy = pendingDatasets.length > 0;

  const renderDatasetRows = (
    rows: ReportingApiEntitlementDataset[],
    emptyMessage: string,
  ) => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 px-4 py-3 pl-10 text-[13px] text-[#5E6278]">
          <Loader2
            className="size-3.5 animate-spin"
            aria-hidden
          />
          Loading data tiers…
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="px-4 py-3 pl-10 text-[13px] text-[#5E6278]">
          {emptyMessage}
        </div>
      );
    }
    return rows.map((row) => {
      const isPending = pendingDatasets.includes(row.dataset);
      return (
        <div
          key={row.dataset}
          className="flex items-center gap-2.5 py-2.5 pl-10 pr-4"
        >
          <SelectionCheckbox
            state={row.entitled ? 'checked' : 'unchecked'}
            disabled={disabled || isPending}
            onClick={() => void handleDatasetToggle(row.dataset, row.entitled)}
          />
          <span className="flex-1 text-[13.5px] font-semibold text-[#0D0F1A]">
            {formatDatasetLabel(row.dataset)}
          </span>
          {isPending ? (
            <Loader2
              className="size-3.5 shrink-0 animate-spin text-[#5E6278]"
              aria-hidden
            />
          ) : null}
        </div>
      );
    });
  };

  return (
    <>
      <div className="border-b border-[#EDEEF2] last:border-b-0">
        <div className="flex items-center gap-2.5 bg-[#F7F8FA] px-4 py-3">
          <SelectionCheckbox
            state={reportingParentState}
            disabled={disabled || isLoading || parentBusy || reportingDatasets.length === 0}
            onClick={() => void handleParentToggle(reportingDatasets, reportingParentState)}
          />
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => toggleExpanded('reporting-api')}
          >
            {reportingExpanded ? (
              <ChevronDown className="size-[15px] shrink-0 text-[#9CA3AF]" />
            ) : (
              <ChevronRight className="size-[15px] shrink-0 text-[#9CA3AF]" />
            )}
            <span className="text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
              Reporting API
            </span>
          </button>
          {isLoading ? (
            <Loader2
              className="size-4 shrink-0 animate-spin text-[#5E6278]"
              aria-hidden
            />
          ) : null}
        </div>

        {reportingExpanded
          ? renderDatasetRows(reportingDatasets, 'No grantable reporting data tiers available.')
          : null}
      </div>

      <div className="border-b border-[#EDEEF2] last:border-b-0">
        <div className="flex items-center gap-2.5 bg-[#F7F8FA] px-4 py-3">
          <SelectionCheckbox
            state={surveysParentState}
            disabled={disabled || isLoading || parentBusy || surveyDatasets.length === 0}
            onClick={() => void handleParentToggle(surveyDatasets, surveysParentState)}
          />
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => toggleExpanded('surveys-api')}
          >
            {surveysExpanded ? (
              <ChevronDown className="size-[15px] shrink-0 text-[#9CA3AF]" />
            ) : (
              <ChevronRight className="size-[15px] shrink-0 text-[#9CA3AF]" />
            )}
            <span className="text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
              Surveys API
            </span>
          </button>
          {isLoading ? (
            <Loader2
              className="size-4 shrink-0 animate-spin text-[#5E6278]"
              aria-hidden
            />
          ) : null}
        </div>

        {surveysExpanded
          ? renderDatasetRows(surveyDatasets, 'No grantable survey data tiers available.')
          : null}
      </div>
    </>
  );
}

/** Shows the API Key card when the client has any active Reporting API entitlement. */
export function ManageClientApiKeysSection() {
  const { activeClient } = useActiveClient();
  const extClientId = activeClient?.ext_client_id;
  const { data: entitlements } = useGetReportingApiEntitlementsQuery(
    extClientId ?? '',
    { skip: !extClientId }
  );

  const hasAnyEntitlement = Boolean(
    entitlements?.datasets.some((row) => row.grantable && row.entitled)
  );

  if (!extClientId || !hasAnyEntitlement) {
    return null;
  }

  return (
    <ManageClientApiKeysCard
      extClientId={extClientId}
      clientTitle={activeClient?.title}
    />
  );
}

export default ManageClientApiAccess;
